// 화면에 보이는 소품·메뉴 이름과 설명 (영어). id는 packages/shared/src/catalog.ts.
// 다른 언어는 같은 모양의 파일을 추가한다.
import type { DishId, ItemId, LagoonMaskId } from '@capy/shared';

export interface ItemText {
  /** 버튼 이름 (이모지 포함). */
  label: string;
  /** 상점에 보이는 이름. 무료 소품은 없음. */
  name?: string;
  blurb?: string;
}

export const ITEM_TEXT: Record<ItemId, ItemText> = {
  orange: { label: '🍊 Orange' },
  yuzu: { label: '🍋 Yuzu', name: 'Yuzu', blurb: 'A fragrant winter yuzu, the classic onsen companion. Quiet Spring only.' },
  duckling: { label: '🐤 Duckling' },
  frog: { label: '🐸 Frog' },
  towel: { label: '🧺 Towel', name: 'Towel', blurb: 'A folded cotton towel for long, steamy soaks. Quiet Spring only.' },
  glacier: { label: '🧊 Glacier peak', name: 'Glacier peak', blurb: 'A clear, sparkling ice peak shaped like Kirkjufell. It steams and drips in the hot lagoon and glows blue at night. Lava Lagoon only.' },
  puffin: { label: '🐧 Puffin', name: 'Puffin', blurb: 'A tiny Atlantic puffin that flaps its wings when the geyser goes off. Lava Lagoon only.' },
  minigeyser: { label: '🌋 Mini geyser', name: 'Mini geyser', blurb: 'A little black volcano with a geyser in its crater. It bubbles, swells and erupts together with the big one, sized to match. Lava Lagoon only.' },
  auroracloud: { label: '🌌 Aurora cloud', name: 'Aurora cloud', blurb: 'A soft little cloud by day. At night it glows and shifts between green and violet like the aurora. Lava Lagoon only.' },
  lopapeysa: { label: '🧶 Lopapeysa beanie', name: 'Lopapeysa beanie', blurb: 'A tall, cosy mint wool beanie with the classic Icelandic snowflake yoke and a big fluffy pom-pom. Lava Lagoon only.' },
  hotdog: { label: '🌭 Icelandic hot dog', name: 'Icelandic hot dog', blurb: 'A Reykjavík-style pylsa with sweet brown mustard, remoulade and crispy onions. Lava Lagoon only.' },
  yangmeori: { label: '🐑 Yangmeori towel', name: 'Yangmeori towel', blurb: 'The classic jjimjilbang look: a towel rolled into little sheep ears. Jjimjilbang only.' },
  stoneegg: { label: '🥚 Baked egg', name: 'Baked egg', blurb: 'One warm, brown, stone-baked egg balanced on top. Jjimjilbang only.' },
  bananamilk: { label: '🍌 Banana milk', name: 'Banana milk', blurb: 'A little bottle of sweet banana milk with a straw, the after-bath favourite. Jjimjilbang only.' },
  bagaji: { label: 'Gourd dipper', name: 'Gourd dipper', blurb: 'A traditional Korean half-gourd water dipper, worn upside down like a hat. Jjimjilbang only.' },
  kbeauty: { label: '✨ K-beauty mask', name: 'K-beauty sheet mask', blurb: 'A dewy white K-beauty sheet mask with cut-outs for the eyes, nose and mouth. Jjimjilbang only.' },
  cucumber: { label: '🥒 Cucumber mask', name: 'Cucumber mask', blurb: 'Two cool cucumber slices over the eyes for a proper jjimjilbang rest. Jjimjilbang only.' },
  bubble: { label: '🫧 Nose bubble', name: 'Nose bubble', blurb: 'A sleepy nose bubble that grows and shrinks with each breath, and swells up when you nap.' },
  scrubmitt: { label: 'Scrub mitt', name: 'Scrub mitt', blurb: 'A green Korean scrub mitt for your paw. Lets you give your tummy a good scrub. Jjimjilbang only.' },
  scratcher: { label: 'Back scratcher', name: 'Back scratcher', blurb: 'A wooden back scratcher shaped like a little hand. Lets you give your head a good scratch. Jjimjilbang only.' },
  baby: { label: '🍼 Baby capybara', name: 'Baby capybara', blurb: 'Turns you into a half-size baby capybara with a slightly bigger head. Works in both rooms.' },
  classic: { label: 'Classic' },
  honey: { label: 'Honey' },
  cocoa: { label: 'Cocoa' },
};

export const MENU_TEXT: Record<DishId | LagoonMaskId, ItemText> = {
  egg: { label: '🥚 Onsen egg', name: 'Onsen egg' },
  dango: { label: '🍡 Hanami dango', name: 'Hanami dango' },
  onigiri: { label: '🍙 Onigiri', name: 'Onigiri' },
  soba: { label: '🍜 Kake soba', name: 'Kake soba' },
  milk: { label: '🥛 Coffee milk', name: 'Coffee milk' },
  smilk: { label: '🍓 Strawberry milk', name: 'Strawberry milk' },
  matcha: { label: '🍵 Matcha and sweet', name: 'Matcha and a sweet' },
  sashimi: { label: '🐟 Sashimi', name: 'Sashimi' },
  sushi: { label: '🍣 Sushi', name: 'Sushi' },
  bento: { label: '🍱 Bento', name: 'Bento' },
  begg: { label: '🥚 Baked egg' },
  kimbap: { label: '{kimbap} Kimbap' },
  ramyeon: { label: '🍜 Ramyeon' },
  buldak: { label: '🌶️ Buldak ramen' },
  chicken: { label: '🍗 Fried chicken' },
  miyeok: { label: '🥣 Seaweed soup' },
  sikhye: { label: '🍚 Sikhye' },
  misu: { label: '🥤 Misutgaru' },
  silica: { label: '🤍 Silica mud' },
  algae: { label: '🌿 Algae mask' },
  lavaclay: { label: '🌋 Lava clay' },
  slushie: { label: '💙 Blue slush' },
  berry: { label: '🍓 Berry slush' },
  mango: { label: '🥭 Mango slush' },
  skyr: { label: '🫐 Skyr smoothie' },
  hotcocoa: { label: '☕ Hot cocoa' },
};

/** 4단계 동안 쓰는 임시 화면 문구. 5단계에서 모바일 UI를 새로 만들 때 다시 정리한다. */
export const UI_TEXT = {
  pickRoom: 'Pick a room',
  rooms: {
    quiet: { name: '♨️ Quiet Spring', blurb: 'A snowy Japanese onsen. No talking, just soaking.' },
    lagoon: { name: '🌋 Lava Lagoon', blurb: 'A milky blue lagoon in Iceland. Chat using eight letters.' },
    jjim: { name: '🧖 Jjimjilbang', blurb: 'A Korean sauna and bathhouse. Sweat, soak, snack and chat.' },
  },
  comingSoon: 'Coming soon',
  goRoom: { quiet: '🏯 Quiet Spring', lagoon: '🌋 Lava Lagoon', jjim: '🧖 Jjimjilbang' },
  canvas: {
    quiet: 'Plush capybaras at a snowy open-air hot spring. Tap to walk or soak, drag to turn, pinch to zoom.',
    lagoon: 'Plush capybaras swimming in a milky blue lagoon in Iceland. Tap to swim, drag to turn, pinch to zoom.',
    jjim: 'Plush capybaras at a Korean sauna and bathhouse.',
  },
  hint: 'Tap to walk or soak. Tap a capybara to climb on. Drag to turn, pinch to zoom.',
  hintIndoors: 'Pick a dish and it will be served at your table.',
  actions: { bathe: '♨️ Bathe', stroll: '🐾 Stroll', wave: '👋 Wave', wobble: 'Wobble', waddle: 'Waddle', yawn: 'Yawn', nap: '💤 Nap', hopdown: '⬇️ Hop down', climbable: '🪜 Others can climb on me' },
  goIn: '🏮 Go inside to eat',
  goOut: '🚪 Go outside',
  full: 'That tower is full. Up to 4 capybaras can stack.',
  refused: 'This capybara doesn’t want anyone on its head right now.',
} as const;

/** 찜질방 안내 문구 (프로토타입 jjimNote·flashHint). {name}·{ko}·{tub}·{n}은 바꿔 넣는다. */
export const JJIM_TEXT = {
  hint: 'Tap a mat, a log or a room door. Drag to turn, pinch to zoom.',
  backInHall: 'Back in the main hall.',
  walkedInto: 'You walked into the {name} ({ko}).',
  bathRule: ' Wash up at a seat or shower before you get in a tub.',
  washFirstHint: 'Wash first at a seat or shower, then you can use the tubs.',
  hotTub: 'Ahh, the hot tub. Tap outside the tub to hop out.',
  coldTub: 'Brr, the cold tub! Tap outside the tub to hop out.',
  onDeck: 'Up on the deck. Tap the floor below to come back down.',
  showerOn: 'Turning on the shower…',
  dipper: 'Splash! Pouring water over yourself with the dipper.',
  shopPick: 'Pick up to four things, then tap Order. It takes about three seconds.',
  shopMax: 'Up to four things at a time.',
  shopEmpty: 'Pick at least one thing from the menu.',
  order: '🧾 Order',
  orderN: '🧾 Order {n}',
  cooking: 'Cooking… {n}',
  toTable: 'Here you go! Taking it to a table.',
  toSpot: 'Here you go! Carrying it back to your spot.',
  allGone: 'All gone. Yum.',
  spicy: 'Spicy! Your face is on fire.',
  nextUp: 'Next up!',
  allGoneGetUp: 'All gone. Tap anywhere to get up.',
  foodComing: 'Your food is on its way. Wait until you sit down to eat.',
  finishFood: 'Finish your food first.',
  waitFood: 'Wait for your food first.',
  babiesOnly: 'The playground is for baby capybaras only.',
  babiesOnlyNote: 'Only baby capybaras can play here.',
  washFirstNote: 'Wash up first, then into the tub.',
  washFirst: 'Wash first, then you can get in the tub.',
  already: 'You are already in the {name}.',
  toHall: 'Heading back to the main hall.',
  toRoom: 'Heading to the {name} ({ko}).',
  deckFull: 'The deck is full right now.',
  toDeck: 'Climbing the stairs to the deck.',
  alongDeck: 'Moving along the deck.',
  allTaken: 'All taken. Try again in a moment.',
  toWash: 'Off to a washing seat.',
  toShower: 'Off to a shower.',
  tubFullTap: 'The {tub} tub is full right now. Try again in a moment.',
  washThenTub: 'Wash up first, then into the {tub} tub.',
  intoTub: 'Hopping into the {tub} tub.',
  taken: 'Someone is already there. Try again soon.',
  play: { slide: 'Up the ladder, then wheee down the slide.', tramp: 'Boing boing on the trampoline.', pit: 'Diving into the ball pit.' },
  orderOnWay: 'You already have an order on the way.',
  toShop: 'Heading to the snack bar.',
  slideAgain: 'Wheee! Tap the slide again for another go.',
  cleanIn: 'All clean. In you go.',
  cleanTubs: 'All clean. Tap the hot or cold tub to get in.',
  noRollDeck: 'No rolling on the upstairs deck. You might fall off!',
  lieAfterEat: 'You can lie down after eating.',
  noLieWater: 'You can’t lie down in the water.',
  rooms: { hall: '🏠 Main hall', bath: '🛁 Bathhouse', hwangto: '🟫 Hwangto room', salt: '🧂 Salt room', ice: '🧊 Ice room' },
  deck: '🪜 Upstairs deck',
  shop: '🛒 Snack bar',
  bath: { wash: '🪣 Wash with a dipper', shower: '🚿 Take a shower', hot: '♨️ Hot tub', cold: '🧊 Cold tub' },
  playBtn: { slide: '🛝 Slide', tramp: '🤸 Trampoline', pit: '🟡 Ball pit' },
  lie: '🛌 Lie down',
  rollL: '⬅️ Roll',
  rollR: 'Roll ➡️',
  scrub: '🧽 Scrub my tummy',
  scratch: '🪵 Scratch my head',
} as const;
/** 문구의 {키}를 값으로 바꾼다. */
export const fill = (s: string, v: Record<string, string | number>): string => s.replace(/\{(\w+)\}/g, (_, k: string) => String(v[k] ?? ''));

/** 라군 안내 문구 (프로토타입 lagoonNote). */
export const LAGOON_TEXT = {
  hint: 'Swim up to the mask bar or the slush bar to see their menus. The sun sets slowly, the aurora comes out at night, and the geyser goes off now and then.',
  clean: 'All clean. Your skin feels silky.',
  tossed: 'Cup tossed. Thanks for keeping the lagoon tidy.',
  maskOn: 'Mask on. Rinse it off under the little waterfall when you’re done.',
  slushOn: 'Here you go. Carry it around and sip. It rides on your head while you swim.',
  toMask: 'Swimming over to the mask bar.',
  toSlush: 'Swimming over to the slush bar.',
  rinseMask: 'Rinsing the mask off under the waterfall.',
  rinse: 'Ahh, a refreshing little shower.',
  toRinse: 'Swimming over to the rinse waterfall.',
  warn: { big: 'The geyser is rumbling hard. Watch the north side!', medium: 'The geyser is bubbling.', small: '' },
  fire: { big: 'Whoosh! A big one!', medium: 'Splash! A good one.', small: 'Pff. A little puff from the geyser.' },
  aurora: 'The aurora is out. Look up!',
  swim: '🌊 Swim',
  bars: { mask: '🧖 Go to the mask bar', slush: '🥤 Go to the slush bar', rinse: '🚿 Rinse off the mask' },
  barTitle: { mask: 'Mask bar', slush: 'Slush bar' },
  free: 'Free',
  toss: '🗑️ Throw away the cup',
  keys: { space: 'space', back: '⌫', send: 'Say it' },
  sayHint: 'Type with a b c i m p r y',
} as const;
